import { memo, useMemo, useState } from 'react';
import { Button } from '../../../atoms/Button/Button';
import { Icon } from '../../../atoms/Icon/Icon';
import { PlatformIcon } from '../../../atoms/PlatformIcon/PlatformIcon';
import { ProgressBar } from '../../../atoms/ProgressBar/ProgressBar';
import { Pagination } from '../../../molecules/Pagination/Pagination';
import { Search } from '../../../molecules/Search/Search';
import { TableHeader } from '../../../molecules/TableHeader/TableHeader';
import type { TableHeaderSort } from '../../../molecules/TableHeaderLabel/TableHeaderLabel';
import { Table, type TableColumn, type TableRowData } from '../../../organisms/Table/Table';
import { spanClass, type WidgetBlockProps } from '../../widgets/shared/cardShell';
import { tableEmptyLabel } from '../shared/tableShared';
import { TableWidgetActions, tableDraggingClass } from '../TableWidgetActions/TableWidgetActions';
import styles from './InfluencerAnalyticsTable.module.css';

/**
 * `Influencer analytics` — the Influencers roster, Figma `964:6974`.
 *
 * The **only** Influencers widget a report can take: every other card in that
 * module is scoped to one creator, and a report has no way to say which.
 * Its rows are the module's own — every tracked creator, range only, with no
 * chip step and no brand step, because brand safety is a judgement about the
 * creator rather than about us.
 *
 * **Client-side sort, search and page**, as in the prototype: the contract
 * returns the whole roster.
 *
 * Deviations from the frame, as the prototype recorded them: columns sort;
 * `Delete` and `Edit` are dropped from the row actions (`View details` is the
 * only one with somewhere to go); no loading variant (`tableEmptyLabel`).
 */

/** Figma draws ten body rows, which is also what the pagination implies. */
const PAGE_SIZE = 10;

/** One creator, with the figures already formatted for their cells. */
export type InfluencerRowView = {
  id: string;
  name: string;
  /** Platform icon names (channel keys), in the creator's own order. */
  platforms: string[];
  /** Each platform's display name, same order — names the mark (and finds it, for a live id). Defaults to the key. */
  platformLabels?: string[];
  followers: number;
  /** `1.28M` — two fraction digits, matching the frame's own column. */
  followersLabel: string;
  posts: number;
  postsLabel: string;
  /** `null` on a creator with nothing in the period — it sorts last both ways. */
  engagementRate: number | null;
  /** `4.2%`, or an em dash when the period cannot answer it. */
  engagementLabel: string;
  safety: number;
  /** The creator's detail page (canonical path). */
  href: string;
};

export type InfluencerAnalyticsView = {
  /** Roster order — by following. */
  influencers: InfluencerRowView[];
};

export type InfluencerSortKey = 'name' | 'followers' | 'posts' | 'engagementRate' | 'safety';
export type InfluencerSort = { key: InfluencerSortKey; direction: 'asc' | 'desc' };

/**
 * Opening order: by following, descending — which is the order the frame's own
 * ten rows are in, and the order the roster is declared in.
 */
export const DEFAULT_INFLUENCER_SORT: InfluencerSort = { key: 'followers', direction: 'desc' };

/** `null` sorts last in both directions: it means "nothing here", not "zero". */
function compare(a: InfluencerRowView, b: InfluencerRowView, key: InfluencerSortKey): number {
  if (key === 'name') return a.name.localeCompare(b.name);
  const left = a[key];
  const right = b[key];
  if (left === null && right === null) return 0;
  if (left === null) return 1;
  if (right === null) return -1;
  return left - right;
}

/** The table's rows for a search and a sort — exported for the parity test. */
export function filterInfluencers(
  rows: readonly InfluencerRowView[],
  query: string,
  sort: InfluencerSort,
): InfluencerRowView[] {
  /*
   * Substring match on the name, case-insensitive. An empty query is every
   * row, which is what the old `ALL` sentinel meant.
   */
  const q = query.trim().toLowerCase();
  const filtered = q === '' ? rows : rows.filter((row) => row.name.toLowerCase().includes(q));
  const sorted = [...filtered].sort((a, b) => compare(a, b, sort.key));
  return sort.direction === 'desc' ? sorted.reverse() : sorted;
}

export type InfluencerAnalyticsTableProps = WidgetBlockProps & {
  view?: InfluencerAnalyticsView;
};

export const InfluencerAnalyticsTable = memo(function InfluencerAnalyticsTable({
  status,
  view,
  span = 12,
  chrome,
}: InfluencerAnalyticsTableProps) {
  const [sort, setSort] = useState<InfluencerSort>(DEFAULT_INFLUENCER_SORT);
  const [page, setPage] = useState(1);
  const [query, setQuery] = useState('');

  const all = useMemo(() => (status === 'ready' && view ? view.influencers : []), [status, view]);
  const ranked = useMemo(() => filterInfluencers(all, query, sort), [all, query, sort]);

  const pageCount = Math.max(1, Math.ceil(ranked.length / PAGE_SIZE));
  /*
   * Clamped on read rather than reset in an effect. Narrowing the search while
   * on page 3 leaves `page` pointing past the end, and an effect correcting it
   * would paint the empty page first.
   */
  const currentPage = Math.min(page, pageCount);
  const visible = ranked.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const sortFor = (key: InfluencerSortKey): TableHeaderSort =>
    sort.key === key ? sort.direction : 'selector';

  const onSort = (key: InfluencerSortKey) => () =>
    setSort((prev) =>
      prev.key === key
        ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : /* A new column opens descending for everything but the name. */
          { key, direction: key === 'name' ? 'asc' : 'desc' },
    );

  /*
   * **No `helpText` on any sortable column** — `TableHeaderCell` wraps the
   * label in its sort `<button>` and `HelpIcon` is itself a `<button>`: a
   * button inside a button. The explanations live in the table header's
   * supporting line instead. Flagged in `docs/rules.md` §7.
   */
  const columns: TableColumn[] = [
    {
      key: 'name',
      header: 'Influencer',
      sort: sortFor('name'),
      onSort: onSort('name'),
      width: '18%',
    },
    /* The marks carry no text, so the column head is the only thing naming them. */
    { key: 'platforms', header: 'Platforms', cellType: 'actionIcons', width: '10%' },
    {
      key: 'followers',
      header: 'Followers',
      sort: sortFor('followers'),
      onSort: onSort('followers'),
      width: '13%',
    },
    {
      key: 'posts',
      header: 'Posts analyzed',
      sort: sortFor('posts'),
      onSort: onSort('posts'),
      width: '14%',
    },
    {
      key: 'engagement',
      header: 'Engagement',
      sort: sortFor('engagementRate'),
      onSort: onSort('engagementRate'),
      width: '13%',
    },
    {
      key: 'safety',
      header: 'Brand safety',
      sort: sortFor('safety'),
      onSort: onSort('safety'),
      cellType: 'progressBar',
      width: '20%',
    },
    { key: 'actions', cellType: 'actionButtons', width: '12%' },
  ];

  const tableRows: TableRowData[] = visible.map((row) => ({
    key: row.id,
    cells: {
      name: { text: row.name },
      platforms: {
        content: (
          <span className={styles.platforms}>
            {row.platforms.map((channel, i) => (
              <PlatformIcon
                key={channel}
                name={channel}
                label={row.platformLabels?.[i] ?? channel}
              />
            ))}
          </span>
        ),
      },
      followers: { text: row.followersLabel },
      posts: { text: row.postsLabel },
      engagement: { text: row.engagementLabel },
      safety: {
        content: (
          <ProgressBar
            value={row.safety}
            size="sm"
            showLabel
            label={`Brand safety for ${row.name}`}
          />
        ),
      },
      actions: {
        content:
          (
            /*
             * A real link (the app's router link, via `Button href`), so the
             * browser navigates and the back button works. Figma draws it as a
             * link-coloured label with a trailing chevron.
             */
            <Button
              href={row.href}
              hierarchy="ghostColor"
              iconTrailing={<Icon name="chevronRight" />}
            >
              View details
            </Button>
          ),
      },
    },
  }));

  return (
    <Table
      className={[spanClass(span), tableDraggingClass(chrome)].filter(Boolean).join(' ')}
      label="Influencer analytics"
      columns={columns}
      rows={tableRows}
      /* The search can filter the roster down to nothing; say so rather than leaving an empty band. */
      emptyLabel={tableEmptyLabel(status, 'No influencer by that name')}
      header={
        <TableHeader
          flash={chrome?.flash}
          title="Influencer analytics"
          helpText="Tracked creators, with their platforms, followers, posts analysed, engagement rate and brand safety score. Brand safety is a standing score that does not change with the date filter."
          actions={<TableWidgetActions chrome={chrome} title="Influencer analytics" />}
          search={
            <Search
              aria-label="Search by influencer name"
              placeholder="Search by influencer name"
              value={query}
              onValueChange={(next) => {
                setQuery(next);
                // A narrower roster can be shorter than the page we are on.
                setPage(1);
              }}
            />
          }
        />
      }
      pagination={
        <Pagination
          page={currentPage}
          pageCount={pageCount}
          onPageChange={setPage}
          label="Influencer pages"
        />
      }
    />
  );
});
